'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { DELIVERY_ADDRESS_COOKIE } from '../constants'
import type { DeliveryAddress } from '../entities'
import { mapDeliveryAddressToCookieAddress } from '../helpers'
import { useLastBrowsedLocation } from '@/location/last-browsed-location/client'
import { analytics } from '@/tools/analytics'
import { consoleLog } from '@/tools/console-log'
import { deleteCookie, setCookie } from '@/tools/cookies'
import { useErrorHandling } from '@/tools/o11y/client'

export type UserAddressContext = {
  deliveryAddress: DeliveryAddress | null
  doesDeliveryAddressMatchLocation: () => boolean
  setDeliveryAddress: (newAddress: DeliveryAddress | null) => Promise<void>
}

const DeliveryAddressContext = createContext<UserAddressContext>({
  deliveryAddress: null,
  doesDeliveryAddressMatchLocation: () => false,
  setDeliveryAddress: () => Promise.resolve(),
})

type DeliveryAddressProviderProps = {
  children: React.ReactNode
  deliveryAddress: DeliveryAddress | null
}

export const DeliveryAddressProvider = ({
  children,
  deliveryAddress,
}: DeliveryAddressProviderProps) => {
  const [internalAddress, setInternalAddress] = useState<DeliveryAddress | null>(deliveryAddress)
  const { lastBrowsedLocation } = useLastBrowsedLocation()
  const { execWithErrorTracking } = useErrorHandling()
  const { setLocationFromDeliveryAddress } = useLastBrowsedLocation()

  const doesDeliveryAddressMatchLocation = () => {
    const deliveryAddressMatchesLocation =
      internalAddress?.cityCode === lastBrowsedLocation.cityCode &&
      internalAddress?.countryCode === lastBrowsedLocation.countryCode
    return deliveryAddressMatchesLocation
  }

  const setDeliveryAddress = async (address: DeliveryAddress | null) => {
    consoleLog.navy('DeliveryAddress', 'Updated', address)
    if (!address) {
      deleteCookie(DELIVERY_ADDRESS_COOKIE)
    } else {
      const cookieAddress = await mapDeliveryAddressToCookieAddress(address)
      const stringifiedCookieAddress = JSON.stringify(cookieAddress)
      setCookie(DELIVERY_ADDRESS_COOKIE, stringifiedCookieAddress)
    }
    analytics.setGlobalAttribute('location', {
      locationCity: address?.cityCode || null,
      locationCountry: address?.countryCode || null,
      locationLat: address?.latitude ? `${address?.latitude}` : null,
      locationLon: address?.longitude ? `${address?.longitude}` : null,
    })
    await setLocationFromDeliveryAddress(address)
    setInternalAddress(address)
  }

  useEffect(() => {
    execWithErrorTracking(() => {
      consoleLog.navy('DeliveryAddress', 'Initialized', internalAddress)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <DeliveryAddressContext.Provider
      value={{
        deliveryAddress: internalAddress,
        doesDeliveryAddressMatchLocation,
        setDeliveryAddress,
      }}
    >
      {children}
    </DeliveryAddressContext.Provider>
  )
}

export const useDeliveryAddress = () => useContext(DeliveryAddressContext)
